import { IconButton, Stack, Text, TooltipHost } from '@fluentui/react';
import React from 'react';

import { useQuery } from '@apollo/client';
import { AccountingEntryInvoice_invoice } from 'ap/signing/accountingEntry/view/__generated__/AccountingEntryInvoice';
import {
  AvailableInvoiceDocumentTypes,
  AvailableInvoiceDocumentTypesVariables,
} from 'ap/signing/transactionSigning/view/FormView/ActionMenu/__generated__/AvailableInvoiceDocumentTypes';
import clsx from 'clsx';
import { AmountTextView } from 'common/components/AmountView/AmountTextView';
import { DocumentDataCallout } from 'common/components/AttachDocumentModal/DocumentDataCallout';
import { TABLE_ROWS } from 'common/constants';
import { EntityDocumentInput } from 'common/types/globalTypes';
import { getGlobalDateFormat } from 'common/utils/dateFormats';
import { useFormContext } from 'react-hook-form';

import { GenericAttachForm } from 'common/components/ActionMenu/Attach/AttachFormModal';
import {
  AppliableInvoiceSigningDocuments,
  AppliableInvoiceSigningDocuments_appliableInvoiceSigningDocuments_nodes,
} from 'common/components/Modules/TransactionEdit/FormView/ActionMenu/AttachForm/__generated__/AppliableInvoiceSigningDocuments';
import { DocumentNode } from 'graphql';
import { columns } from './column.data';
import { useStyles } from './index.styles';
import { toOrderByVariable } from './utils';

import INVOICE_DETAILS from '../../../../AccountingEntryInvoice.graphql';
import APPLIABLE_INVOICE_SIGNING_DOCUMENTS from '../../../../../../../../common/components/Modules/TransactionEdit/FormView/ActionMenu/AttachForm/AppliableInvoiceSigningDocuments.graphql';

import APPLY_INVOICE_SIGNING_DOCUMENTS from '../../../../../../transactionSigning/view/FormView/ActionMenu/actionMenuContents/Attach/AttachForm/ApplyInvoiceSigningDocument.graphql';
import AVAILABLE_DOCUMENT_TYPES from '../../../../../../transactionSigning/view/FormView/ActionMenu/AvailableInvoiceDocumentTypes.graphql';

type AttachableDataType =
  AppliableInvoiceSigningDocuments_appliableInvoiceSigningDocuments_nodes;

interface AttachFormProps {
  invoice: AccountingEntryInvoice_invoice;
}

export const AttachForm: React.FC<AttachFormProps> = (props) => {
  const {
    formState: { isDirty },
  } = useFormContext();

  const { data: AvailableDocumentTypes } = useQuery<
    AvailableInvoiceDocumentTypes,
    AvailableInvoiceDocumentTypesVariables
  >(AVAILABLE_DOCUMENT_TYPES, {
    variables: {
      transactionTypeId: props.invoice.transactionTypeId,
      isDocumentUpload: false,
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-only',
  });

  const transformedEntity = {
    ...props.invoice,
    isDraft: props.invoice.isDraft ?? undefined,
  };
  const styles = useStyles();
  return (
    <>
      <GenericAttachForm
        entity={transformedEntity}
        isDirty={isDirty}
        availableDocumentTypes={AvailableDocumentTypes}
        attachableDocumentsQuery={
          APPLIABLE_INVOICE_SIGNING_DOCUMENTS as DocumentNode
        }
        attachDocumentsMutation={
          APPLY_INVOICE_SIGNING_DOCUMENTS as DocumentNode
        }
        detailsQuery={INVOICE_DETAILS as DocumentNode}
        columns={columns}
        modalWidth={1450}
        getDocumentTypeOptions={(availableDocumentTypes) => {
          return (
            availableDocumentTypes?.invoiceSigningAvailableDocumentTypes?.nodes.map(
              (doctype) => ({
                key: doctype.id,
                text: doctype.documentType || '',
                title:
                  (!props.invoice.isDraft &&
                    doctype.isAccountingDocument &&
                    'Upload prohibited during/after the approval process') ||
                  undefined,
                disabled:
                  (!props.invoice.isDraft && doctype.isAccountingDocument) ||
                  undefined,
              })
            ) || []
          );
        }}
        transformData={(data: AppliableInvoiceSigningDocuments | undefined) => {
          return (
            data?.appliableInvoiceSigningDocuments?.nodes.map(
              (documents) =>
                ({
                  ...documents,
                  available: documents.documentAppliedAmounts?.remainingTotal,
                  isoCode: documents.currency?.isoCode,
                  selected: false,
                } as AttachableDataType)
            ) || []
          );
        }}
        canSelectItem={(item: AttachableDataType) => {
          const canSelect = props.invoice.isDraft
            ? true
            : !item._isAccountingDocument;
          return canSelect;
        }}
        getMutationInput={(
          selectedItems: AttachableDataType[],
          entityId: string
        ) => {
          const entityDocuments = selectedItems.map(
            (item) =>
              ({
                id: item.id,
                comment: item.comment,
                entityId: item.entityId,
                fileReference: item.fileReference,
                fileIndexInformation: item.fileIndexInformation,
                entityDocumentTypeId: item.entityDocumentTypeId,
                indexName: item.indexName,
                indexDescription: item.indexDescription,
                indexTransactionDate: item.indexTransactionDate,
                indexAmount: item.indexAmount,
                indexCurrencyId: item.indexCurrencyId,
                documentAppliedAmount: item.documentAppliedAmount,
                documentStatusExplanation: item.documentStatusExplanation,
                isAppliedAmountRetired: item.isAppliedAmountRetired,
                isAppliedAmountOverage: item.isAppliedAmountOverage,
              } as EntityDocumentInput)
          );
          return {
            input: {
              invoiceId: entityId,
              entityDocuments,
            },
          };
        }}
        onRenderItemColumn={(item, _index, column, openDocumentViewModal) => {
          if (!item || !column) return undefined;

          const fieldContent = item[
            column.fieldName as keyof AttachableDataType
          ] as string | null;

          switch (column.key) {
            case 'indexAmount':
            case 'available':
              return (
                <Stack className={styles.columnHeight} verticalAlign="center">
                  <AmountTextView
                    className={clsx(
                      item._isAccountingDocument ? styles.disabledText : '',
                      styles.contentColumnAlignRight
                    )}
                    value={fieldContent}
                  />
                </Stack>
              );
            case 'used':
              return (
                <Stack className={styles.columnHeight} verticalAlign="center">
                  <AmountTextView
                    className={clsx(
                      item._isAccountingDocument ? styles.disabledText : '',
                      styles.contentColumnAlignRight
                    )}
                    value={item.documentAppliedAmounts?.usedTotal ?? ''}
                  />
                </Stack>
              );
            case 'view':
              const viewDocumentVisible =
                item._isProtected || item._fileViewer !== 'browser';
              return (
                <Stack
                  className={styles.columnHeight}
                  tokens={{ childrenGap: 10 }}
                  horizontal
                  verticalAlign="center"
                >
                  <TooltipHost content="View" id="tooltipId">
                    <IconButton
                      disabled={viewDocumentVisible}
                      iconProps={{ iconName: 'View' }}
                      onClick={() =>
                        openDocumentViewModal?.({
                          isOpen: true,
                          title: item.fileReference || 'Document',
                          entityDocumentId: item.id,
                          _fileType: item._fileType || 'pdf',
                        })
                      }
                    />
                  </TooltipHost>
                </Stack>
              );
            case 'fileReference':
              return (
                <Stack className={styles.columnHeight} verticalAlign="center">
                  <DocumentDataCallout
                    disabled={item._isAccountingDocument || false}
                    item={item}
                  />
                </Stack>
              );
            case '_uploadDate':
              return (
                <Stack className={styles.columnHeight} verticalAlign="center">
                  <Text
                    className={
                      item._isAccountingDocument ? styles.disabledText : ''
                    }
                  >
                    {item._uploadDate
                      ? getGlobalDateFormat(item._uploadDate)
                      : ''}
                  </Text>
                </Stack>
              );
            default:
              return (
                <Stack className={styles.columnHeight} verticalAlign="center">
                  <Text
                    className={
                      item._isAccountingDocument ? styles.disabledText : ''
                    }
                  >
                    {fieldContent}
                  </Text>
                </Stack>
              );
          }
        }}
        initialQueryVariables={{
          first: TABLE_ROWS,
          invoiceId: props.invoice.id,
          orderBy: ['_UPLOAD_DATE_DESC', 'PRIMARY_KEY_DESC'],
        }}
        toOrderByVariable={toOrderByVariable}
      />
    </>
  );
};
